import React, { useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { MOBILE_UI } from '../../constants/mobileUi';

const HEIGHTS = { sm: 32, md: MOBILE_UI.controlHeight };

/**
 * Two-to-three option switch with a sliding-pill look. Use for in-place mode switches
 * (view mode, sheet stages, sort direction); section navigation stays on WebullStyleTabs.
 *
 * @param {Array} options - `[{ value, label, badge? }]`
 */
const SegmentedControl = ({
  options = [],
  value,
  onChange,
  size = 'md',
  fullWidth = true,
  ariaLabel,
  testId,
  role = 'group',
  scrollable = false,
  /** 'subtle' reads as an inset pill; 'accent' fills the active option with the brand color. */
  variant = 'subtle',
}) => {
  const { colors, isLight } = useTheme();
  const height = HEIGHTS[size] || HEIGHTS.md;
  const scrollerRef = useRef(null);
  const dragRef = useRef(null);
  // Set when a drag ends so the click that follows the release does not select.
  const suppressClickRef = useRef(false);

  const handlePointerDown = (event) => {
    if (!scrollable || (event.button != null && event.button !== 0)) return;
    // pointerdown is followed by a compatibility mousedown. Restarting the
    // gesture there drops the pointer id and the drag already in progress.
    if (event.pointerId == null && dragRef.current) return;
    // A drag whose click never reached a button must not block the next press.
    suppressClickRef.current = false;
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startScrollLeft: scrollerRef.current?.scrollLeft || 0,
      moved: false,
      pointerType: event.pointerType || 'mouse',
      pointerId: event.pointerId,
      captured: false,
    };
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag) return;
    if (drag.pointerId != null && event.pointerId != null && event.pointerId !== drag.pointerId) return;
    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(deltaX, deltaY) > 6) {
      drag.moved = true;
      // Capture only after the gesture is a drag. Capturing on pointerdown
      // retargets the click to this scroller, so the option button never runs.
      const scroller = scrollerRef.current;
      if (
        drag.pointerType === 'mouse'
        && event.pointerId != null
        && scroller
        && typeof scroller.setPointerCapture === 'function'
      ) {
        try {
          scroller.setPointerCapture(event.pointerId);
          drag.captured = true;
        } catch (_) { /* jsdom / non-pointer */ }
      }
    }
    if (drag.moved && drag.pointerType === 'mouse' && scrollerRef.current) {
      event.preventDefault();
      scrollerRef.current.scrollLeft = drag.startScrollLeft - deltaX;
    }
  };

  const handlePointerUp = (event) => {
    const drag = dragRef.current;
    if (!drag) return;
    if (drag.pointerId != null && event.pointerId != null && event.pointerId !== drag.pointerId) return;
    if (drag.moved) suppressClickRef.current = true;
    const scroller = scrollerRef.current;
    if (
      drag.captured
      && event.pointerId != null
      && scroller
      && typeof scroller.releasePointerCapture === 'function'
      && scroller.hasPointerCapture?.(event.pointerId)
    ) {
      try {
        scroller.releasePointerCapture(event.pointerId);
      } catch (_) { /* already released */ }
    }
    dragRef.current = null;
  };

  return (
    <div
      ref={scrollerRef}
      role={role}
      aria-label={ariaLabel}
      data-testid={testId}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => { dragRef.current = null; }}
      onMouseDown={handlePointerDown}
      onMouseMove={handlePointerMove}
      onMouseUp={handlePointerUp}
      onClickCapture={(event) => {
        if (!suppressClickRef.current) return;
        event.preventDefault();
        event.stopPropagation();
      }}
      onClick={() => {
        // Click landed on the strip (pointer capture) rather than a button.
        if (suppressClickRef.current) suppressClickRef.current = false;
      }}
      style={{
        display: fullWidth ? 'flex' : 'inline-flex',
        gap: 4,
        width: fullWidth ? '100%' : undefined,
        padding: 3,
        borderRadius: MOBILE_UI.radiusControl,
        backgroundColor: isLight ? colors.bgSecondary : 'rgba(255,255,255,0.06)',
        boxSizing: 'border-box',
        overflowX: scrollable ? 'auto' : undefined,
        scrollbarWidth: scrollable ? 'none' : undefined,
      }}
    >
      {options.map((option) => {
        const selected = option.value === value;
        const accent = variant === 'accent';
        return (
          <button
            key={option.value}
            type="button"
            role={role === 'tablist' ? 'tab' : undefined}
            aria-selected={role === 'tablist' ? selected : undefined}
            aria-pressed={role === 'tablist' ? undefined : selected}
            data-testid={option.testId}
            onClick={() => {
              if (suppressClickRef.current) {
                suppressClickRef.current = false;
                return;
              }
              if (selected) return;
              onChange?.(option.value);
            }}
            style={{
              flex: fullWidth && !scrollable ? 1 : '0 0 auto',
              minWidth: 0,
              height: height - 6,
              border: 'none',
              borderRadius: MOBILE_UI.radiusControl - 2,
              padding: '0 12px',
              fontSize: size === 'sm' ? 12 : MOBILE_UI.font.body,
              fontWeight: selected ? 700 : 600,
              cursor: 'pointer',
              backgroundColor: selected ? (accent ? colors.primary : colors.bgPrimary) : 'transparent',
              color: selected ? (accent ? '#fff' : colors.text) : colors.textMuted,
              boxShadow: selected && !accent ? '0 1px 3px rgba(0,0,0,0.18)' : 'none',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 5,
              whiteSpace: 'nowrap',
            }}
          >
            <span>{option.label}</span>
            {option.badge || null}
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedControl;
